import { useQueryClient } from '@tanstack/react-query';
import * as React from 'react';
import { RemoteEventsViewModel } from '../../extViewmodels/RemoteEventsViewModel';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { invalidateTicketLists } from './supportQueryHelpers';

// A single reply fans out several ticket writes; coalesce them into one refetch.
const INVALIDATION_DEBOUNCE_MS = 100;

export function useSupportTicketsRealtime(mailboxId: string) {
	const userSession = useUserSession();
	const queryClient = useQueryClient();
	const accountId = userSession?.account?.id;
	const userId = userSession?.user?.id;

	React.useEffect(() => {
		if (!accountId || !userId || !mailboxId) return undefined;

		const remoteEvents = new RemoteEventsViewModel(userSession);
		let disposed = false;
		let invalidateTimer: ReturnType<typeof setTimeout> | undefined;
		let subscription: { dispose: () => void } | undefined;

		remoteEvents
			.connect()
			.then(() => {
				if (disposed) {
					void remoteEvents.permanentlyDisconnect();
					return;
				}

				subscription = remoteEvents.addEventHandler(`Mailbox.${mailboxId}.SupportTicket.#`, () => {
					if (disposed) return;
					if (invalidateTimer) clearTimeout(invalidateTimer);
					invalidateTimer = setTimeout(() => {
						invalidateTimer = undefined;
						void invalidateTicketLists(queryClient);
					}, INVALIDATION_DEBOUNCE_MS);
				});
			})
			.catch(() => {
				// Queries still refetch on focus, and local sends invalidate their affected lists.
			});

		return () => {
			disposed = true;
			if (invalidateTimer) clearTimeout(invalidateTimer);
			subscription?.dispose();
			void remoteEvents.permanentlyDisconnect();
		};
	}, [accountId, mailboxId, queryClient, userId, userSession]);
}
